import React, { useState } from "react";
import { useTheme } from "../../../../Utils/useTheme";
import { Column } from "../types";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const ColumnReorderBar = ({
  columns,
  onReorder,
}: {
  columns: Column<any>[];
  onReorder: (fromKey: string, toKey: string) => void;
}) => {
  const { border, text, hover } = useTheme();
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(e.active.id as string)}
      onDragEnd={(e) => {
        const from = e.active.id as string;
        const to = e.over?.id as string;
        setActiveId(null);
        if (from && to && from !== to) onReorder(from, to);
      }}
      onDragCancel={() => setActiveId(null)}
    >
      <div
        style={{
          padding: "10px 12px",
          background: "rgb(107 13 253 / 8%)",
          borderBottom: `1px solid ${border}`,
          overflowX: "auto",
          whiteSpace: "nowrap",
        }}
      >
        <SortableContext
          items={columns.map((c) => c.key)}
          strategy={horizontalListSortingStrategy}
        >
          <div
            style={{
              display: "inline-flex",
              gap: 10,
              minWidth: "max-content",
            }}
          >
            {columns.map((c) => (
              <SortableItem
                key={c.key}
                id={c.key}
                title={c.title}
                border={border}
                text={text}
                hover={hover}
              />
            ))}
          </div>
        </SortableContext>
      </div>

      <DragOverlay>
        {activeId ? (
          <DragChip
            title={columns.find((x) => x.key === activeId)?.title}
            border={border}
            text={text}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

const SortableItem = ({
  id,
  title,
  border,
  text,
  hover,
}: {
  id: string;
  title: string;
  border: string;
  text: string;
  hover: string;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        position: "relative",
        padding: "6px 12px",
        borderRadius: 999,
        border: `1px solid ${border}`,
        background: "rgba(39,17,39,0.06)",
        fontSize: 12,
        color: text,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: "grab",
        userSelect: "none",
      }}
      {...attributes}
      {...listeners}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        style={{ opacity: 0.9 }}
      >
        <path
          d="M9 6h1M9 12h1M9 18h1M14 6h1M14 12h1M14 18h1"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      {title}
    </div>
  );
};

const DragChip = ({
  title,
  border,
  text,
}: {
  title?: string;
  border: string;
  text: string;
}) => (
  <div
    style={{
      padding: "8px 14px",
      borderRadius: 999,
      border: `1px solid ${border}`,
      background: "rgba(120,80,160,0.25)",
      color: text,
      fontSize: 12,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      boxShadow: "0 14px 30px rgba(0,0,0,.28)",
      transform: "scale(1.08)",
    }}
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M9 6h1M9 12h1M9 18h1M14 6h1M14 12h1M14 18h1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
    {title}
  </div>
);
